import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, EmptyState } from "../components/app/Common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { fmtDate, fmtTime } from "../lib/utils";
import { Clock, DollarSign, MapPin, Sparkles, Handshake, X, User } from "lucide-react";

const STATUS_CHIP = {
  open: "bg-muted text-foreground border-border",
  allocated: "bg-blue-500/10 text-blue-500 border-blue-500/40",
  confirmed: "bg-[hsl(var(--status-pass))/0.1] text-[hsl(var(--status-pass))] border-[hsl(var(--status-pass))/0.4]",
  pending_approval: "bg-[hsl(var(--status-hold))/0.1] text-[hsl(var(--status-hold))] border-[hsl(var(--status-hold))/0.4]",
};

export default function MyShifts() {
  const { user } = useAuth();
  const [mine, setMine] = useState([]);
  const [open, setOpen] = useState([]);
  const [profile, setProfile] = useState({ skills: "", hourly_rate: 0, availability_status: "available" });

  const load = useCallback(async () => {
    const [m, o] = await Promise.all([
      api.get("/allocations/mine?upcoming=true"),
      api.get("/shifts?status=open"),
    ]);
    setMine(m.data);
    setOpen(o.data);
    setProfile({
      skills: (user?.skills || []).join(", "),
      hourly_rate: user?.hourly_rate || 0,
      availability_status: user?.availability_status || "available",
    });
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const requestShift = async (sid) => {
    try {
      await api.post(`/shifts/${sid}/request`);
      toast.success("Requested — waiting for manager approval");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const dropShift = async (aid) => {
    if (!confirm("Request to drop this shift?")) return;
    try {
      await api.post(`/allocations/${aid}/drop`);
      toast.success("Drop request sent to manager");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const saveProfile = async () => {
    try {
      await api.patch("/users/me/profile", {
        skills: profile.skills.split(",").map((s) => s.trim()).filter(Boolean),
        hourly_rate: Number(profile.hourly_rate) || 0,
        availability_status: profile.availability_status,
      });
      toast.success("Profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <PageHeader
        title="My Shifts"
        subtitle="Your upcoming allocated shifts and the board of open shifts you can claim."
      />

      {/* Profile / availability */}
      <div className="border border-border bg-card rounded-sm p-5 mb-8" data-testid="profile-panel">
        <div className="flex items-center gap-2 mb-3">
          <User className="h-4 w-4 text-primary" />
          <div className="overline">Profile & availability</div>
        </div>
        <div className="grid md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="overline mb-1 block">Skills (comma separated)</Label>
            <Input value={profile.skills} onChange={(e) => setProfile({ ...profile, skills: e.target.value })} className="rounded-sm font-mono" placeholder="e.g. rigger, forklift, first-aid" data-testid="profile-skills-input" />
          </div>
          <div>
            <Label className="overline mb-1 block">Hourly rate</Label>
            <Input type="number" step="0.5" value={profile.hourly_rate} onChange={(e) => setProfile({ ...profile, hourly_rate: e.target.value })} className="rounded-sm font-mono" data-testid="profile-rate-input" />
          </div>
          <div>
            <Label className="overline mb-1 block">Availability</Label>
            <Select value={profile.availability_status} onValueChange={(v) => setProfile({ ...profile, availability_status: v })}>
              <SelectTrigger className="rounded-sm" data-testid="profile-avail-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="available">available</SelectItem>
                <SelectItem value="limited">limited</SelectItem>
                <SelectItem value="unavailable">unavailable</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="mt-3">
          <Button onClick={saveProfile} className="rounded-sm bg-primary hover:bg-primary/90" data-testid="profile-save-btn">Save profile</Button>
        </div>
      </div>

      {/* My upcoming shifts */}
      <div className="mb-10">
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="overline mb-1">Upcoming</div>
            <h2 className="text-xl font-semibold tracking-tight">My shifts</h2>
          </div>
          <div className="text-xs font-mono text-muted-foreground">{mine.length} scheduled</div>
        </div>
        {mine.length === 0 ? (
          <EmptyState title="No upcoming shifts" body="Claim an open shift below to get started." />
        ) : (
          <div className="grid md:grid-cols-2 gap-3" data-testid="my-shifts-list">
            {mine.map((a) => (
              <div key={a.id} className="border border-border bg-card rounded-sm p-4" data-testid={`my-shift-${a.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <Badge variant="outline" className={`rounded-sm font-mono uppercase tracking-widest text-[10px] ${STATUS_CHIP[a.status]}`}>
                      {a.status.replace("_", " ")}
                    </Badge>
                    <div className="font-semibold text-base mt-2">{a.shift.title}</div>
                    <div className="text-[11px] font-mono text-muted-foreground mt-1 flex flex-wrap gap-2">
                      <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(a.shift.start_time)} · {fmtTime(a.shift.start_time)}–{fmtTime(a.shift.end_time)}</span>
                      <span className="inline-flex items-center gap-1"><DollarSign className="h-3 w-3" /> {a.shift.hourly_rate}/h</span>
                    </div>
                  </div>
                  {a.status === "confirmed" && (
                    <Button variant="outline" size="sm" className="rounded-sm border-border" onClick={() => dropShift(a.id)} data-testid={`drop-${a.id}`}>
                      <X className="h-3.5 w-3.5 mr-1" /> Drop
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Available open shifts */}
      <div>
        <div className="flex items-end justify-between mb-3">
          <div>
            <div className="overline mb-1">Available</div>
            <h2 className="text-xl font-semibold tracking-tight">Open shifts board</h2>
          </div>
          <div className="text-xs font-mono text-muted-foreground">{open.length} open</div>
        </div>
        {open.length === 0 ? (
          <EmptyState title="No open shifts" body="Check back later — managers post open shifts here." />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="open-shifts-list">
            {open.map((s) => {
              const alreadyRequested = s.allocations.some(
                (a) => a.user_id === user?.id && a.status === "pending_approval",
              );
              return (
                <div key={s.id} className="border border-border bg-card rounded-sm p-4 hover:border-primary/50 transition-colors" data-testid={`open-shift-${s.id}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    <span className="overline">OPEN</span>
                  </div>
                  <div className="font-semibold text-base">{s.title}</div>
                  <div className="text-[11px] font-mono text-muted-foreground mt-1 space-y-0.5">
                    <div className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {fmtDate(s.start_time)}</div>
                    <div>{fmtTime(s.start_time)} → {fmtTime(s.end_time)}</div>
                    <div className="flex gap-3">
                      <span className="inline-flex items-center gap-1"><DollarSign className="h-3 w-3" /> {s.hourly_rate}/h</span>
                      <span>role {s.role_required}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="rounded-sm bg-primary hover:bg-primary/90 w-full mt-3"
                    onClick={() => requestShift(s.id)}
                    disabled={alreadyRequested}
                    data-testid={`request-shift-${s.id}`}
                  >
                    <Handshake className="h-3.5 w-3.5 mr-1" />
                    {alreadyRequested ? "Requested" : "Request shift"}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}